import { useStore } from '@nanostores/react';
import { useCallback, type UIEvent } from 'react';
import { toast } from 'react-toastify';
import { FatalErrorDialogContent } from '~/components/dialogs/fatal-error';
import { ReferralsDialogContent } from '~/components/dialogs/referrals';
import { SettingsDialog } from '~/components/dialogs/settings/SettingsDialog';
import { ConfirmLogoutDialogContent } from '~/components/ui/ConfirmLogoutDialogContent';
import { DeleteChatDialogContent } from '~/components/ui/DeleteChatDialogContent';
import { Dialog } from '~/components/ui/Dialog';
import type { ChatHistoryItem } from '~/lib/persistence/chats';
import { chatStore } from '~/lib/stores/chat';
import { domainsStore } from '~/lib/stores/domains';
import { menuStore } from '~/lib/stores/menu';
import { projectStore } from '~/lib/stores/project';
import { teamStore } from '~/lib/stores/team';
import { logger } from '~/utils/logger';

export const Dialogs = () => {
  const dialogContent = useStore(menuStore.dialogContent);

  const closeDialog = useCallback(() => {
    menuStore.closeDialog();
    teamStore.selectedMembers.set([]);
    teamStore.updateTeamData.set(null);
    domainsStore.resetAll();

    const url = new URL(window.location.href);
    url.searchParams.forEach((_, key) => {
      url.searchParams.delete(key);
    });
    window.history.replaceState({}, '', url);
  }, []);

  const deleteItem = useCallback(async (event: UIEvent, item: ChatHistoryItem) => {
    event.preventDefault();

    try {
      await chatStore.removeChat(item);

      if (projectStore.getProjectId() === item.projectId) {
        // hard page navigation to clear the stores
        window.location.pathname = '/';
      }

      toast.success('Chat deleted');
    } catch (error) {
      toast.error('Failed to delete chat');

      logger.error(error);
    }
  }, []);

  const handleDeleteChat = useCallback(
    async (event: UIEvent) => {
      if (!dialogContent || dialogContent?.type !== 'delete') {
        return;
      }

      await deleteItem(event, dialogContent.item);
      closeDialog();
    },
    [deleteItem, closeDialog, dialogContent],
  );

  return (
    <>
      <Dialog
        open={!!dialogContent && dialogContent.type !== 'settings'}
        onClose={closeDialog}
        showCloseButton={dialogContent?.type !== 'experimental-browser'}
        closeOnBackdropClick={dialogContent?.type !== 'experimental-browser'}
        contentClassName={`selection-accent max-w-[512px] ${dialogContent?.type === 'referrals' ? 'lg:max-w-lg' : ''}`}
      >
        {dialogContent?.type === 'delete' && (
          <DeleteChatDialogContent
            projectId={dialogContent.item.projectId}
            itemDescription={dialogContent.item.description}
            onDeleteItem={handleDeleteChat}
            onClose={closeDialog}
          />
        )}
        {dialogContent?.type === 'referrals' && <ReferralsDialogContent closeDialog={closeDialog} />}
        {dialogContent?.type === 'fatal-error' && <FatalErrorDialogContent {...dialogContent} />}
        {dialogContent?.type === 'confirm-logout' && <ConfirmLogoutDialogContent onClose={closeDialog} />}
      </Dialog>

      <SettingsDialog
        isOpen={dialogContent?.type === 'settings'}
        closeDialog={closeDialog}
        initialPage={dialogContent && 'initialPage' in dialogContent ? dialogContent.initialPage : undefined}
        screen={dialogContent && 'screen' in dialogContent ? dialogContent.screen : undefined}
      />
    </>
  );
};
